/** @jsx jsx */
import { type SerializedStyles, css, jsx } from '@emotion/react';

import * as React from 'react';

import { Chip, breakpoints } from '@coursera/cds-core';

type Spacing = 'small' | 'medium' | 'compact';
type BackgroundColorVariant = 'lightBlue' | 'secondary';

type WithSvgIconVariant = {
  variant: 'withSvgIcon';
  iconBackgroundColor?: string;
  icon: React.ReactElement;
  spacing?: Spacing;
};

type WithImageIconVariant = {
  variant: 'withImageIcon';
  icon: React.ReactElement;
};

type TextOnlyVariant = {
  variant: 'textOnly';
  spacing?: Spacing;
};

export type OptionPillProps = {
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  backgroundColorVariant?: BackgroundColorVariant;
  dataTestId?: string;
} & (WithSvgIconVariant | WithImageIconVariant | TextOnlyVariant);

const styles = {
  withSvgIcon: ({ iconBackgroundColor }: { iconBackgroundColor: string }) => css`
    .cds-Chip-label .cds-Chip-startIcon {
      background-color: ${iconBackgroundColor};
    }
  `,
  withSvgIconMedium: css`
    &.cds-Chip-root {
      padding-right: var(--cds-spacing-200);
    }

    .cds-Chip-label .cds-Chip-startIcon {
      width: 28px;
      height: 28px;
      margin-right: var(--cds-spacing-100);

      svg {
        width: var(--cds-spacing-200);
        height: var(--cds-spacing-200);
      }
    }

    ${breakpoints.down('xs')} {
      &.cds-Chip-root {
        padding-right: var(--cds-spacing-100);

        .cds-Chip-label .cds-Chip-startIcon {
          width: var(--cds-spacing-200);
          height: var(--cds-spacing-200);
          margin-right: var(--cds-spacing-50);

          svg {
            width: 10px;
            height: 10px;
          }
        }
      }
    }
  `,
  withSvgIconSmall: css`
    &.cds-Chip-root {
      padding: var(--cds-spacing-100) var(--cds-spacing-150);

      .cds-Chip-label .cds-Chip-startIcon {
        height: auto;
        width: auto;
        margin-right: var(--cds-spacing-50);

        svg {
          width: var(--cds-spacing-200);
          height: var(--cds-spacing-200);
        }
      }
    }
  `,
  withImageIcon: css`
    &.cds-Chip-root {
      .cds-Chip-label .cds-Chip-startIcon {
        width: var(--cds-spacing-200);
        height: var(--cds-spacing-200);
        margin-right: var(--cds-spacing-50);

        img {
          border-radius: 50%;
          width: var(--cds-spacing-200);
          height: var(--cds-spacing-200);
        }
      }
    }
  `,
  textOnly: ({ spacing }: { spacing: 'small' | 'medium' | 'compact' }) => css`
    &.cds-Chip-root {
      padding: ${spacing === 'compact'
        ? '2px var(--cds-spacing-100)'
        : `var(--cds-spacing-100) ${spacing === 'small' ? 'var(--cds-spacing-100)' : 'var(--cds-spacing-150)'}`};
      ${spacing === 'compact' && `border: none; height: 24px; min-height: unset;`}
    }
  `,
  lightBlueBackground: css`
    &.cds-Chip-root {
      border-color: var(--cds-color-emphasis-primary-background-xweak);
      background-color: var(--cds-color-emphasis-primary-background-xweak);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-interactive-primary-hover);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-interactive-primary-hover);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-interactive-primary-pressed);
      background-color: var(--cds-color-interactive-background-primary-pressed-weak);
      border-color: var(--cds-color-interactive-primary-pressed);
    }
  `,
  secondaryBackground: css`
    &.cds-Chip-root {
      transition: border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease;
      background-color: var(--cds-color-interactive-background-secondary-selected-weak);
    }

    &.cds-Chip-root:hover {
      background-color: var(--cds-color-interactive-background-secondary-hover-weak);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-neutral-primary-invert);
      background-color: var(--cds-color-interactive-secondary-selected);
    }
  `,
  customChip: css`
    &.cds-Chip-root {
      padding: var(--cds-spacing-100);
      border-color: var(--cds-color-grey-50);
      font-weight: 400;

      .cds-Chip-label .cds-Chip-startIcon {
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-grey-900);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-interactive-primary-hover);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-interactive-primary);
      background-color: var(--cds-color-neutral-background-primary);
      border-color: var(--cds-color-interactive-primary);
    }

    &.cds-Chip-root:disabled {
      color: var(--cds-color-grey-500);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
};

const backgroundVariantStyles = {
  lightBlue: styles.lightBlueBackground,
  secondary: styles.secondaryBackground,
} as const;

/**
 * CDS Chip component with custom styles. This should be used in a CDS ChipGroup.
 */
const OptionPill = (props: OptionPillProps) => {
  const { label, selected, onClick, variant, disabled, backgroundColorVariant, dataTestId } = props;
  let variantStyles: SerializedStyles[];

  switch (variant) {
    case 'withSvgIcon': {
      const { iconBackgroundColor = 'var(--cds-color-emphasis-primary-background-strong)', spacing = 'medium' } = props;
      variantStyles = [
        styles.withSvgIcon({ iconBackgroundColor }),
        spacing === 'small' ? styles.withSvgIconSmall : styles.withSvgIconMedium,
      ];
      break;
    }

    case 'withImageIcon':
      variantStyles = [styles.withImageIcon];
      break;

    case 'textOnly': {
      const { spacing = 'medium' } = props;
      variantStyles = [styles.textOnly({ spacing })];
      break;
    }

    default:
      variantStyles = [];
  }

  return (
    <Chip
      icon={variant !== 'textOnly' ? props.icon : undefined}
      onClick={onClick}
      variant="select"
      selected={selected}
      disabled={disabled}
      data-testid={dataTestId}
      css={[
        styles.customChip,
        ...variantStyles,
        backgroundColorVariant && backgroundVariantStyles[backgroundColorVariant],
      ]}
    >
      {label}
    </Chip>
  );
};

export default OptionPill;
